import { groupBy, isEqual, keys } from 'lodash'
import { useMemo } from 'react'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { AdPlatformMappingForm } from 'app/components/mapping/AdPlatformMappingForm'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { AdPlatformMappingType, defaultAdPlatformMappings } from 'lib/adPlatformDefaults'
import { logError } from 'lib/logging'
import { useAdPlatformMappingStore, useEntityList } from 'app/stores/StoreContext'

export type MappingPlatformsLoaderData = {
  platforms: { hits: AdPlatformMappingType[] }
  query: string | null
  canEditMapping: boolean
}

export type GroupedPlatforms = Record<string, AdPlatformMappingType[]>

export type UseMappingPlatformsReturn = {
  groupedPlatforms: GroupedPlatforms
  platformNames: string[]
  loading: boolean
  canEditMapping: boolean
  onAdd: () => void
  onEdit: (platform: AdPlatformMappingType) => void
  onDelete: (id: string, platform: string) => Promise<void>
  isDefaultMapping: (platform: AdPlatformMappingType) => boolean
}

export function useMappingPlatforms(): UseMappingPlatformsReturn {
  const [confirm] = useConfirmation()
  const checkPermission = useCheckPermission()
  const canEditMapping = checkPermission([`org:settings:edit-mapping`, `settings:mapping:edit`])
  const store = useAdPlatformMappingStore()
  const query: string | null = null

  const { list, loading } = useEntityList(store)
  const platforms = list as unknown as AdPlatformMappingType[]

  const defaultAdPlatforms = useMemo(
    () => defaultAdPlatformMappings.map(([source, medium, platform]) => ({ source, medium, platform })),
    [],
  )

  const groupedPlatforms = useMemo(
    () => groupBy([...platforms, ...defaultAdPlatforms], `platform`),
    [platforms, defaultAdPlatforms],
  )

  const platformNames = useMemo(() => keys(groupedPlatforms).sort(), [groupedPlatforms])

  const isDefaultMapping = (p: AdPlatformMappingType) =>
    defaultAdPlatformMappings.some((d) => isEqual(d, [p.source, p.medium, p.platform]))

  const openModal = ({ title, platform }: { title: string; platform?: AdPlatformMappingType }) =>
    showModal(
      <AdPlatformMappingForm platform={platform} isDefaultPlatform={platform ? isDefaultMapping(platform) : false} />,
      { title, isFullScreen: false, removePadding: true, useRadix: true },
    )

  const onAdd = () => openModal({ title: `Create New Platform Mapping` })
  const onEdit = (platform: AdPlatformMappingType) => openModal({ title: `Edit Platform Mapping`, platform })

  const onDelete = async (id: string, _platform: string) => {
    await confirm({
      title: `Delete Ad Platform Mapping`,
      description: `Are you sure you want to delete this ad platform mapping?`,
      variant: `danger`,
    }).then(async () => {
      const result = await store.remove(id)
      if (result.error) {
        logError(`Error deleting ad platform mapping`, result.error)
        notifyError(`Error deleting platform mapping`)
      } else {
        notifySuccess(`Deleted Successfully`)
        store.fetchList()
      }
    })
  }

  return {
    groupedPlatforms,
    platformNames,
    loading,
    canEditMapping,
    onAdd,
    onEdit,
    onDelete,
    isDefaultMapping,
  }
}
